import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { Loader2, UserPlus } from 'lucide-react'
import { useCreateProfessional } from '../hooks/use-professionals'
import { useToast } from '@/hooks/use-toast'
import { PROFESSIONAL_SPECIALTIES } from '../constants/professional-specialties'

const createProfessionalSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email invalido'),
  specialty: z.string().optional(),
  role: z.enum(['admin', 'professional']),
})

type CreateProfessionalFormValues = z.infer<typeof createProfessionalSchema>

interface CreateProfessionalModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateProfessionalModal({
  open,
  onOpenChange,
}: CreateProfessionalModalProps) {
  const { toast } = useToast()
  const createProfessional = useCreateProfessional()

  const form = useForm<CreateProfessionalFormValues>({
    resolver: zodResolver(createProfessionalSchema),
    defaultValues: {
      name: '',
      email: '',
      specialty: '',
      role: 'professional',
    },
  })

  const onSubmit = async (data: CreateProfessionalFormValues) => {
    try {
      await createProfessional.mutateAsync(data)
      toast({
        title: 'Profissional criado',
        description: 'Um email com as credenciais foi enviado.',
      })
      form.reset()
      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Erro',
        description: (error as Error).message ?? 'Nao foi possivel criar o profissional.',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Novo Profissional
          </DialogTitle>
          <DialogDescription>
            Adicione um novo profissional. As credenciais de acesso serao enviadas por email.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome completo</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: Maria Silva"
                      autoComplete="name"
                      className="h-11"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="email@exemplo.com"
                      autoComplete="email"
                      inputMode="email"
                      className="h-11"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    As credenciais serao enviadas para este email
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="specialty"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Especialidade</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Selecione uma especialidade" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PROFESSIONAL_SPECIALTIES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Funcao</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Selecione uma funcao" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="professional">Profissional</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Administradores podem gerenciar profissionais e pacientes
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto h-11 sm:h-9"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createProfessional.isPending}
                className="w-full sm:w-auto h-11 sm:h-9"
              >
                {createProfessional.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {createProfessional.isPending ? 'Criando...' : 'Criar Profissional'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
